import InPlayer from '@inplayer-org/inplayer.js';
import { SubmissionError } from 'redux-form';
import includes from 'lodash/includes';
import { handleSocketMessage, parseJSON, checkCode } from '../utils';
import { notifRequested } from '../middleware/notifications/actions';
import { setForm, closeModal, emitEvent } from './index';
import { PRICE_OPTS_FORM } from '../constants/forms';

export const REGISTER_USER_SUCCESS = 'REGISTER_USER_SUCCESS';
export const REGISTER_USER_REQUEST = 'REGISTER_USER_REQUEST';
export const REGISTER_USER_FAILURE = 'REGISTER_USER_FAILURE';

export const registerUser = data => {
    return async (dispatch, getState) => {
        const state = getState();
        const auth = state.auth;
        const clientId = state.paywall.clientID;
        const isTriggerExternal = state.modal.isTriggerExternal;

        const standardKeys = [
            'email',
            'full_name',
            'password',
            'password_confirmation',
            'terms',
            'registerFields',
        ];
        const metadata = Object.keys(data).reduce((obj, key) => {
            const emptyFalseData =
                data[key] === '' ||
                data[key] === 'false' ||
                data[key] === false;
            if (!standardKeys.includes(key) && !emptyFalseData) {
                if (data[key] === true || data[key] === 'true') obj[key] = 'on';
                else obj[key] = data[key];
            }

            return obj;
        }, {});

        if (auth.isAuthenticated) {
            return null;
        }

        const parsedData = {
            email: data.email,
            password: data.password,
            passwordConfirmation: data.password_confirmation,
            clientId,
            type: 'consumer',
            referrer: window.location.origin,
            metadata,
        };

        if (data.full_name) {
            parsedData.fullName = data.full_name;
        } else {
            parsedData.fullName = `${data.first_name} ${data.surname}`;
        }

        dispatch({ type: REGISTER_USER_REQUEST });

        try {
            InPlayer.Account.signUp(parsedData).then(data => {
                if (checkCode(data)) {
                    dispatch({ type: REGISTER_USER_SUCCESS, data });
                    dispatch(emitEvent('authenticated', data));
                    if (!isTriggerExternal) dispatch(setForm(PRICE_OPTS_FORM));
                    else dispatch(closeModal());
                } else {
                    dispatch(
                        notifRequested({
                            title: 'Error',
                            content: data.message,
                            type: 'warning',
                        })
                    );
                    dispatch({ type: REGISTER_USER_FAILURE, data });
                }
            });
        } catch (error) {
            let data = await parseJSON(error.response);
            dispatch(
                notifRequested({
                    title: 'Error',
                    content: data.message,
                    type: 'warning',
                })
            );
            dispatch({ type: REGISTER_USER_FAILURE });
            throw new SubmissionError({ _error: data, ...data.errors });
        }
    };
};

export const LOGIN_USER_REQUEST = 'LOGIN_USER_REQUEST';
export const LOGIN_USER_SUCCESS = 'LOGIN_USER_SUCCESS';
export const LOGIN_USER_FAILURE = 'LOGIN_USER_FAILURE';

export const loginUser = (email, password, uuid) => {
    return async (dispatch, getState) => {
        const state = getState();
        const auth = state.auth;
        const isTriggerExternal = state.modal.isTriggerExternal;

        if (auth.isAuthenticated) {
            return null;
        }

        dispatch({ type: LOGIN_USER_REQUEST });

        try {
            InPlayer.Account.authenticate({
                email,
                password,
                clientId: uuid,
                referrer: '',
            }).then(data => {
                if (checkCode(data)) {
                    dispatch({ type: LOGIN_USER_SUCCESS, data });
                    dispatch(emitEvent('authenticated', data));

                    if (!InPlayer.isSubscribed()) {
                        InPlayer.subscribe(data.account.uuid, {
                            onMessage: message => {
                                handleSocketMessage(message, dispatch);
                            },
                            onOpen: () => {},
                        });
                    }
                } else {
                    dispatch({ type: LOGIN_USER_FAILURE, data });
                    dispatch(
                        notifRequested({
                            title: 'Error',
                            content: ` ${
                                data.message
                            } Please check your email and password and try again.`,
                            type: 'warning',
                        })
                    );
                }
            });
        } catch (error) {
            let data = await parseJSON(error.response);
            dispatch({ type: LOGIN_USER_FAILURE });
            dispatch(
                notifRequested({
                    title: 'Oops',
                    content: ` Something unexpected happened, please try again.`,
                    type: 'warning',
                })
            );
            throw new SubmissionError({ _error: data, ...data.errors });
        }
    };
};

export const USER_REQUEST = 'USER_REQUEST';
export const USER_SUCCESS = 'USER_SUCCESS';
export const USER_FAILURE = 'USER_FAILURE';

export const loadUser = () => {
    return async (dispatch, getState) => {
        const user = getState().auth.user;
        if (user) {
            if (!InPlayer.isSubscribed()) {
                InPlayer.subscribe(user.uuid, {
                    onMessage: message => {
                        handleSocketMessage(message, dispatch);
                    },
                    onOpen: () => {},
                });
            }
            return null;
        }

        dispatch({ type: USER_REQUEST });

        try {
            InPlayer.Account.getAccountInfo(
                InPlayer.Account.getToken().token
            ).then(data => {
                if (!InPlayer.isSubscribed()) {
                    InPlayer.subscribe(data.uuid, {
                        onMessage: message => {
                            handleSocketMessage(message, dispatch);
                        },
                        onOpen: () => {},
                    });
                }
                dispatch({ type: USER_SUCCESS, data });
                dispatch(
                    emitEvent('authenticated', {
                        account: data,
                        ...InPlayer.Account.getToken(),
                    })
                );
            });
            // --> notifyUser function is undefined
            // dispatch(notifyUser(data.uuid));
        } catch (error) {
            dispatch({ type: USER_FAILURE });
        }
    };
};

export const LOGOUT_USER_REQUEST = 'LOGOUT_USER_REQUEST';
export const LOGOUT_USER_SUCCESS = 'LOGOUT_USER_SUCCESS';
export const LOGOUT_USER_FAILURE = 'LOGOUT_USER_FAILURE';

export const logoutUser = callback => {
    return dispatch => {
        const token = InPlayer.Account.getToken().token;
        if (!token) {
            return null;
        }

        dispatch({ type: LOGOUT_USER_REQUEST });

        try {
            InPlayer.Account.signOut().then(data => {
                InPlayer.unsubscribe();
                if (data) {
                    localStorage.removeItem('inplayer_token'); //TODO: hardcoded - add SDK method to remove tokens.
                    localStorage.removeItem('inplayer_iot');
                }

                dispatch({ type: LOGOUT_USER_SUCCESS });
                if (callback) {
                    callback();
                }

                dispatch(emitEvent('logout'));
            });
        } catch (error) {
            localStorage.removeItem('inplayer_token'); //TODO: hardcoded - add SDK method to remove tokens.
            localStorage.removeItem('inplayer_iot');
            dispatch({ type: LOGOUT_USER_FAILURE });
        }
    };
};

export const UPDATE_USER_REQUEST = 'UPDATE_USER_REQUEST';
export const UPDATE_USER_SUCCESS = 'UPDATE_USER_SUCCESS';
export const UPDATE_USER_FAILURE = 'UPDATE_USER_FAILURE';

export const updateMetadata = (data, accountId) => {
    return dispatch => {
        dispatch({ type: UPDATE_USER_REQUEST });

        try {
            InPlayer.Account.updateAccount(
                {
                    metadata: data,
                },
                InPlayer.Account.getToken().token
            ).then(data => {
                dispatch({ type: UPDATE_USER_SUCCESS, data });
            });
        } catch (error) {
            dispatch({ type: UPDATE_USER_FAILURE, error });
        }
    };
};

export const CHANGE_PASSWORD_REQUEST = 'CHANGE_PASSWORD_REQUEST';
export const CHANGE_PASSWORD_SUCCESS = 'CHANGE_PASSWORD_SUCCESS';
export const CHANGE_PASSWORD_FAILURE = 'CHANGE_PASSWORD_FAILURE';

export const changePassword = (oldPassword, newPassword, repeatPassword) => {
    return async dispatch => {
        dispatch({ type: CHANGE_PASSWORD_REQUEST });

        try {
            InPlayer.Account.changePassword(
                {
                    oldPassword: oldPassword,
                    password: newPassword,
                    passwordConfirmation: repeatPassword,
                },
                InPlayer.Account.getToken().token
            ).then(data => {
                if (checkCode(data)) {
                    dispatch(
                        notifRequested({
                            title: 'Success',
                            content:
                                'Your password has been successfully updated',
                            type: 'success',
                        })
                    );
                    dispatch({ type: CHANGE_PASSWORD_SUCCESS, payload: data });
                } else {
                    const objKeys = Object.keys(data.errors);
                    let content = '';
                    objKeys.forEach(key => {
                        content += ' ' + data.errors[key];
                    });

                    dispatch(
                        notifRequested({
                            title: 'Oops..',
                            content:
                                'We were unable to update your password because' +
                                content.toLowerCase(),
                            type: 'warning',
                        })
                    );
                    dispatch({ type: CHANGE_PASSWORD_FAILURE, payload: data });
                }
            });
        } catch (error) {
            let data = await parseJSON(error.response);
            dispatch({ type: CHANGE_PASSWORD_FAILURE });
            throw new SubmissionError({ _error: data, ...data.errors });
        }
    };
};

export const FORGOT_PASSWORD_REQUEST = 'FORGOT_PASSWORD_REQUEST';
export const FORGOT_PASSWORD_SUCCESS = 'FORGOT_PASSWORD_SUCCESS';
export const FORGOT_PASSWORD_FAILURE = 'FORGOT_PASSWORD_FAILURE';

export function requestNewPassword(email, uuid, callback) {
    return dispatch => {
        dispatch({ type: FORGOT_PASSWORD_REQUEST });

        try {
            InPlayer.Account.requestNewPassword({
                email,
                merchantUuid: uuid,
            }).then(data => {
                dispatch({ type: FORGOT_PASSWORD_SUCCESS });
                callback();
            });
        } catch (error) {
            dispatch({ type: FORGOT_PASSWORD_FAILURE });
        }
    };
}

export const SET_PASSWORD_REQUEST = 'SET_PASSWORD_REQUEST';
export const SET_PASSWORD_SUCCESS = 'SET_PASSWORD_SUCCESS';
export const SET_PASSWORD_FAILURE = 'SET_PASSWORD_FAILURE';

export function setNewPassword(
    password,
    passwordConfirmation,
    token,
    callback
) {
    return async dispatch => {
        dispatch({ type: SET_PASSWORD_REQUEST });

        try {
            InPlayer.Account.setNewPassword(
                {
                    password,
                    passwordConfirmation,
                },
                token
            ).then(data => {
                if (checkCode(data)) {
                    dispatch(
                        notifRequested({
                            title: 'Success',
                            content:
                                'Your password has been successfully reset',
                            type: 'success',
                        })
                    );
                    dispatch({ type: SET_PASSWORD_SUCCESS, data });
                    callback();
                } else {
                    const objKeys = Object.keys(data.errors);
                    let content = '';
                    objKeys.forEach(key => {
                        content += ' ' + data.errors[key];
                    });

                    dispatch(
                        notifRequested({
                            title: 'Oops..',
                            content: content,
                            type: 'warning',
                        })
                    );
                    dispatch({ type: SET_PASSWORD_FAILURE, data });
                }
            });
        } catch (error) {
            let data = await parseJSON(error.response);
            dispatch({ type: SET_PASSWORD_FAILURE });
            throw new SubmissionError({ data: data });
        }
    };
}

export const GET_SOCIAL_REQUEST = 'GET_SOCIAL_REQUEST';
export const GET_SOCIAL_SUCCESS = 'GET_SOCIAL_SUCCESS';
export const GET_SOCIAL_FAILURE = 'GET_SOCIAL_FAILURE';

export const getSocialUrls = () => {
    return (dispatch, getState) => {
        const auth = getState().auth;
        if (auth.isSocialFetched || auth.socialUrls.length > 0) {
            return null;
        }

        dispatch({ type: GET_SOCIAL_REQUEST });

        let social_state = btoa(
            JSON.stringify({
                client_id: getState().paywall.clientID,
                redirect: window.location.href,
            })
        );

        try {
            InPlayer.Account.getSocialLoginUrls(social_state).then(data => {
                if (!data.code) {
                    dispatch({
                        type: GET_SOCIAL_SUCCESS,
                        payload: data.social_urls,
                    });
                } else {
                    dispatch({ type: GET_SOCIAL_FAILURE });
                }
            });
        } catch (error) {
            dispatch({ type: GET_SOCIAL_FAILURE });
        }
    };
};

export const GET_REGISTER_REQUEST = 'GET_REGISTER_REQUEST';
export const GET_REGISTER_SUCCESS = 'GET_REGISTER_SUCCESS';
export const GET_REGISTER_FAILURE = 'GET_REGISTER_FAILURE';

//TODO: opt
export const getRegisterFields = () => {
    return (dispatch, getState) => {
        const auth = getState().auth;
        if (auth.isRegisterFetched || auth.registerFields !== null) {
            return null;
        }

        dispatch({ type: GET_REGISTER_REQUEST });

        let merchant_uuid = getState().paywall.clientID;

        try {
            InPlayer.Account.getRegisterFields(merchant_uuid).then(data => {
                dispatch({ type: GET_REGISTER_SUCCESS, payload: data });
            });
        } catch (error) {
            dispatch({ type: GET_REGISTER_FAILURE });
        }
    };
};

export const UPDATE_ACCOUNT_REQUEST = 'UPDATE_ACCOUNT_REQUEST';
export const UPDATE_ACCOUNT_SUCCESS = 'UPDATE_ACCOUNT_SUCCESS';
export const UPDATE_ACCOUNT_FAILURE = 'UPDATE_ACCOUNT_FAILURE';

export const updateAccount = (fullName, metadata) => {
    if (fullName === undefined)
        fullName = `${metadata.first_name} ${metadata.surname}`;

    return dispatch => {
        dispatch({ type: UPDATE_ACCOUNT_REQUEST });

        try {
            InPlayer.Account.updateAccount(
                {
                    fullName,
                    metadata,
                },
                InPlayer.Account.getToken().token
            ).then(data => {
                if (checkCode(data)) {
                    dispatch(
                        notifRequested({
                            title: 'Success',
                            content: 'Your account has been updated',
                            type: 'success',
                        })
                    );
                    dispatch({ type: UPDATE_ACCOUNT_SUCCESS, payload: data });
                } else {
                    dispatch(
                        notifRequested({
                            title: 'Oops..',
                            content:
                                'There was an error while updating your account',
                            type: 'warning',
                        })
                    );
                    dispatch({ type: UPDATE_ACCOUNT_FAILURE });
                }
            });
        } catch (e) {
            dispatch({ type: UPDATE_ACCOUNT_FAILURE });
        }
    };
};

export const GET_PURCHASE_HISTORY_REQUEST = 'GET_PURCHASE_HISTORY_REQUEST';
export const GET_PURCHASE_HISTORY_SUCCESS = 'GET_PURCHASE_HISTORY_SUCCESS';
export const GET_PURCHASE_HISTORY_FAILURE = 'GET_PURCHASE_HISTORY_FAILURE';

//TODO: Check if same page and optimize if possible
export const fetchPurchaseHistory = (status, page, isInitial, limit = 5) => {
    return (dispatch, getState) => {
        dispatch({ type: GET_PURCHASE_HISTORY_REQUEST });

        try {
            InPlayer.Account.getPurchaseHistory(
                InPlayer.Account.getToken().token,
                status,
                page,
                limit
            ).then(data => {
                dispatch({
                    type: GET_PURCHASE_HISTORY_SUCCESS,
                    payload: {
                        data: data,
                        status: status,
                        isInitial: isInitial,
                        page: page,
                    },
                });
            });
        } catch (e) {
            dispatch({ type: GET_PURCHASE_HISTORY_FAILURE });
        }
    };
};

export const SET_LANGUAGE = 'SET_LANGUAGE';
export function setLanguage(language) {
    return dispatch => {
        return dispatch({
            type: SET_LANGUAGE,
            language,
        });
    };
}

export const CHANGE_ACCESS_STATUS = 'CHANGE_ACCESS_STATUS';
export function changeStatus(status) {
    return dispatch => {
        return dispatch({
            type: CHANGE_ACCESS_STATUS,
            status,
        });
    };
}

export const EXPORT_DATA_REQUEST = 'EXPORT_DATA_REQUEST';
export const EXPORT_DATA_SUCCESS = 'EXPORT_DATA_SUCCESS';
export const EXPORT_DATA_FAILURE = 'EXPORT_DATA_FAILURE';
export function exportData(password, callback) {
    return dispatch => {
        dispatch({ type: EXPORT_DATA_REQUEST });

        try {
            InPlayer.Account.exportData(
                InPlayer.Account.getToken().token,
                password
            ).then(data => {
                if (checkCode(data)) {
                    dispatch(
                        notifRequested({
                            title: 'Exported successfully',
                            content: 'Your data has been send to your email',
                            type: 'success',
                        })
                    );

                    dispatch({
                        type: EXPORT_DATA_SUCCESS,
                        payload: {
                            data: data,
                        },
                    });

                    callback();
                } else {
                    dispatch(
                        notifRequested({
                            title: 'Oops..',
                            content:
                                'There was an error while exporting your data',
                            type: 'warning',
                        })
                    );
                    dispatch({ type: EXPORT_DATA_FAILURE });
                }
            });
        } catch (e) {
            dispatch(
                notifRequested({
                    title: 'Oops..',
                    content: 'There was an error while exporting your data',
                    type: 'warning',
                })
            );
            dispatch({ type: EXPORT_DATA_FAILURE });
        }
    };
}

export const DELETE_ACCOUNT_REQUEST = 'DELETE_ACCOUNT_REQUEST';
export const DELETE_ACCOUNT_SUCCESS = 'DELETE_ACCOUNT_SUCCESS';
export const DELETE_ACCOUNT_FAILURE = 'DELETE_ACCOUNT_FAILURE';
export function deleteAccount(password) {
    return dispatch => {
        dispatch({ type: DELETE_ACCOUNT_REQUEST });
        try {
            InPlayer.Account.deleteAccount(
                InPlayer.Account.getToken().token,
                password
            ).then(data => {
                if (checkCode(data)) {
                    InPlayer.Account.signOut().then(data => {
                        dispatch({ type: LOGOUT_USER_SUCCESS });
                    });

                    dispatch(
                        notifRequested({
                            title: 'Deleted successfully',
                            content: 'Your account has been deleted',
                            type: 'success',
                        })
                    );
                } else {
                    dispatch(
                        notifRequested({
                            title: 'Oops..',
                            content:
                                'There was an error while deleting your account',
                            type: 'warning',
                        })
                    );
                    dispatch({ type: DELETE_ACCOUNT_FAILURE });
                }
            });
        } catch (e) {
            dispatch(
                notifRequested({
                    title: 'Oops..',
                    content: 'There was an error while deleting your account',
                    type: 'warning',
                })
            );
            dispatch({ type: DELETE_ACCOUNT_FAILURE });
        }
    };
}

export const RESET_EXPORT_MESSAGE = 'RESET_EXPORT_MESSAGE';
export function resetExportMessage() {
    return dispatch => {
        dispatch({
            type: RESET_EXPORT_MESSAGE,
        });
    };
}



// WEBPACK FOOTER //
// ./src/actions/user.js